import type { CSSProperties, ReactNode } from "react";

/**
 * Sprint 48.33 (foundation polish) — shared filter chip primitive.
 *
 * Loops 48.9 (AlertInboxPresets), 48.29 (MaintenancePresets), 48.31
 * (FuelEventsPage active-vehicle-filter), and 48.32 (RecentErrorsPage
 * level filter) all shipped the same `<button>` shape with slightly
 * different inline `chipStyle(active)` helpers. The Loop 48.32 closeout
 * called out the AGENTS.md §10 rule #4 trigger (4× duplication) and
 * Loop 48.33 retires the drift by extracting the primitive here so
 * both `apps/web` and `apps/admin` can import the same component.
 *
 * The API is deliberately tiny:
 *   - `active`: boolean (drives accent fill vs neutral outline).
 *   - `onClick`: () => void.
 *   - `testId`: page-scoped data-testid; each consumer keeps its
 *     existing test surface (`alert-inbox-preset-*`,
 *     `maintenance-preset-*`, `recent-errors-level-*`, etc.).
 *   - `ariaLabel`: optional override (chips without visible text need it).
 *   - `children`: chip label (usually a translated string).
 *   - `size`: `"sm"` (default, 12px) | `"md"` (14px / DS sm token) for
 *     callers like FuelEventsPage that use the larger chip variant.
 *
 * Styling matches the "solid accent" variant that 3 of 4 existing
 * consumers use (FuelEventsPage / MaintenanceStatusFilter / RecentErrorsPage).
 * AlertInboxPresets uses a slightly different "soft tinted" variant
 * and stays local for now — a future loop can fold it in by adding
 * a `tone="soft"` prop if a 2nd soft consumer materialises.
 */
export interface FilterChipProps {
  readonly active: boolean;
  readonly onClick: () => void;
  readonly testId: string;
  readonly ariaLabel?: string;
  readonly children: ReactNode;
  readonly size?: "sm" | "md";
  /**
   * Sprint 48.39 — chip tone variant.
   * - `"solid"` (default): active = solid accent fill, white text.
   *   Used by MaintenanceStatusFilter, RecentErrorsPage.
   * - `"soft"`: active = soft-tinted accent background, accent text.
   *   Used by FuelEventsPage (less attention-grabbing in dense filter
   *   rows where many chips are visible).
   */
  readonly tone?: "solid" | "soft";
}

export function FilterChip({
  active,
  onClick,
  testId,
  ariaLabel,
  children,
  size = "sm",
  tone = "solid",
}: FilterChipProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      aria-label={ariaLabel}
      data-testid={testId}
      style={chipStyle(active, size, tone)}
    >
      {children}
    </button>
  );
}

function chipStyle(active: boolean, size: "sm" | "md", tone: "solid" | "soft"): CSSProperties {
  return {
    appearance: "none",
    cursor: "pointer",
    // Devin Review on PR #505 caught DESIGN_SYSTEM_RULES.md §2.3 +
    // AGENTS.md §11 violation (no hardcoded pixels/font sizes in
    // primitives). Both `sm` and `md` variants use tokens.
    fontSize: size === "md" ? "var(--gt-sys-font-size-sm)" : "var(--gt-sys-font-size-xs)",
    paddingBlock: "var(--gt-sys-space-1)",
    paddingInline: size === "md" ? "var(--gt-sys-space-3)" : "var(--gt-sys-space-2)",
    borderRadius: 999,
    border: active ? "1px solid var(--gt-sys-accent)" : "1px solid var(--gt-sys-border)",
    background: active
      ? tone === "soft"
        ? "color-mix(in oklab, var(--gt-sys-accent) 12%, transparent)"
        : "var(--gt-sys-accent)"
      : tone === "soft"
        ? "var(--gt-sys-surface)"
        : "transparent",
    color: active
      ? tone === "soft"
        ? "var(--gt-sys-accent)"
        : "var(--gt-sys-on-accent, white)"
      : "var(--gt-sys-text)",
    fontWeight: active ? 600 : 500,
    whiteSpace: "nowrap",
  };
}
